<template>
  <div>
    <DataTable
      v-model:filters="filters"
      :value="items"
      :loading="loading"
      :rows="10"
      :totalRecords="total"
      :rowsPerPageOptions="[10, 25, 50]"
      class="p-datatable-sm"
      dataKey="id"
      filterDisplay="row"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      currentPageReportTemplate="Memuat {first} dari {last} data dari total {totalRecords}"
      row-hover
      paginator
      scrollable
      lazy
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
    >
      <template #empty>
        <div class="text-center">Tidak ada data.</div>
      </template>
      <template #loading>
        <div class="text-center">Memuat data, harap tunggu...</div>
      </template>
      <Column
        dataType="date"
        filter-field="pajak_between"
        field="pi_at"
        header="Tanggal"
        style="min-width: 20rem"
        headerStyle="min-width: 20rem"
      >
        <template #body="{ data }">
          {{ formatDate(data.pi_at) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <CalendarFilter
            ref="calendarFilter"
            v-model="filterModel.value"
            @select="filterCallback()"
          />
        </template>
      </Column>
      <Column field="pi_no" header="No Faktur" style="min-width: 15rem">
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>
      <Column
        field="pajak_no"
        filter-field="pajak_no"
        header="No Seri Pajak"
        style="min-width: 15rem"
      >
      <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>
      <Column
        field="supplier.nama"
        sort-field="supplier"
        filter-field="supplier"
        header="Supplier"
        style="min-width: 20rem"
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>
      <Column
        field="dpp_pi"
        header="DPP"
        style="min-width: 12rem; flex-grow: 1"
      >
        <template #body="{ data }">
          {{ formatCurrency(data.dpp_pi) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
            @keypress="numberOnly"
          />
        </template>
      </Column>
      <Column
        field="ppn_pi"
        header="PPN"
        style="min-width: 12rem; flex-grow: 1"
      >
        <template #body="{ data }">
          {{ formatCurrency(data.ppn_pi) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
            @keypress="numberOnly"
          />
        </template>
      </Column>
      <Column
        field="pph_pi"
        header="PPH"
        style="min-width: 12rem; flex-grow: 1"
      >
        <template #body="{ data }">
          {{ formatCurrency(data.pph_pi) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
            @keypress="numberOnly"
          />
        </template>
      </Column>
      <Column
        field=""
        header="DPP Retur"
        style="min-width: 8rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          {{ !data.pr_no ? formatCurrency(0) : formatCurrency(data.dpp_pr) }}
        </template>
      </Column>
      <Column
        field=""
        header="PPN Retur"
        style="min-width: 9rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          {{ !data.pr_no ? formatCurrency(0) : formatCurrency(data.ppn_pr) }}
        </template>
      </Column>
      <Column
        field=""
        header="PPH Retur"
        style="min-width: 9rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          {{ !data.pr_no ? formatCurrency(0) : formatCurrency(data.pph_pr) }}
        </template>
      </Column>
      <Column 
        field=""
        header="DPP Pajak"
        style="min-width: 9rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          {{ formatCurrency(data.dpp_pjk) }}
        </template>
      </Column>
      <Column
        field=""
        header="PPN Pajak"
        style="min-width: 9rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          {{ formatCurrency(data.ppn_pjk) }}
        </template>
      </Column>
      <Column
        field="masa_pajak"
        filter-field="pajak_masukan.masa_pajak"
        header="Masa Pajak"
        style="min-width: 15.5rem; flex-grow: 1;"
      >
        <template #body="{ data }">
          {{ data.masa_pajak_desc }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <Dropdown
            v-model="filterModel.value"
            :options="listMasaPajak"
            :showClear="true"
            optionValue="id"
            optionLabel="nama"
            class="p-column-filter"
            @change="filterCallback()"
          />
        </template>
      </Column>
      <Column
        field="masa_retur"
        filter-field="pajak_masukan.masa_retur"
        header="Masa Retur"
        style="min-width: 15.5rem; flex-grow: 1;"
      >
        <template #body="{ data }">
          {{ data.masa_retur_desc }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <Dropdown
            v-model="filterModel.value"
            :options="listMasaRetur"
            :showClear="true"
            optionValue="id"
            optionLabel="nama"
            class="p-column-filter"
            @change="filterCallback()"
          />
        </template>
      </Column>
      <Column
        field="tahun_retur"
        filter-field="pajak_masukan.tahun_retur"
        header="Tahun Retur"
        style="min-width: 12rem; flex-grow: 1;"
      >
        <template #body="{ data }">
          {{ data.tahun_retur == 0 ? "" : data.tahun_retur }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            class="p-column-filter"
            :maxLength="4"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>
      <Column
        style="
          display: flex;
          flex-grow: 1;
          justify-content: flex-end;
          min-width: 8rem;
        "
      >
        <template #body="{ data }">
          <Button
            type="button"
            icon="pi pi-pencil"
            class="p-button-outlined p-button-secondary p-button-text"
            @click="$emit('edit', data)"
          />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { FilterMatchMode } from 'primevue/api'
import { formatCurrency, formatDate } from '@/helpers'
import CalendarFilter from '@/components/CalendarFilter'

export default {
  props: {
    items: {
      type: Array,
      default() {
        return []
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: 0,
    },
    gridOptions: {
      type: Object,
      default() {
        return {}
      },
    },
    listMasaPajak: {
      type: Array,
      default() {
        return []
      }
    },
    listMasaRetur: {
      type: Array,
      default() {
        return []
      }
    }
  },
  components: {
    CalendarFilter,
  },
  data() {
    return {
      options: this.gridOptions,
      filters: {
        pi_no: { value: null, matchMode: FilterMatchMode.CONTAINS },
        pajak_no: { value: null, matchMode: FilterMatchMode.CONTAINS },
        supplier: { value: null, matchMode: FilterMatchMode.CONTAINS },
        dpp_pi: { value: null, matchMode: FilterMatchMode.CONTAINS },
        ppn_pi: { value: null, matchMode: FilterMatchMode.CONTAINS },
        pph_pi: { value: null, matchMode: FilterMatchMode.CONTAINS },
        pajak_between: { value: null, matchMode: FilterMatchMode.CONTAINS },
        'pajak_masukan.masa_pajak': { value: null, matchMode: FilterMatchMode.EQUALS },
        'pajak_masukan.masa_retur': { value: null, matchMode: FilterMatchMode.EQUALS },
        'pajak_masukan.tahun_retur': { value: null, matchMode: FilterMatchMode.EQUALS },
      },
    }
  },
  watch: {
    'options.filters'(value) {
      if (!value.pajak_between.value) {
        this.$refs.calendarFilter.clearValueOnly()
      }
    },
  },
  methods: {
    numberOnly(e) {
      if (e.keyCode > 31 && (e.keyCode < 48 || e.keyCode > 57)) {
        e.preventDefault()
      } else {
        return true
      }
    },
    onPage(e) {
      e.page = e.page + 1
      e.sortField = this.options.sortField
      e.sortOrder = this.options.sortOrder
      this.options = e
      this.options.filters = this.filters
      this.loadData()
    },
    onSort(e) {
      this.options = e
      this.loadData()
    },
    onFilter() {
      this.options.page = 1
      this.options.filters = this.filters
      this.loadData()
    },
    loadData() {
      this.$emit('request', this.options)
    },
    formatCurrency(value, minDigit = 0, maxDigit = 0) {
      return formatCurrency(value, minDigit, maxDigit)
    },
    formatDate(value) {
      return formatDate(value)
    },
  },
}
</script>
